import { signalStoreFeature, withState, withComputed, withMethods, patchState } from '@ngrx/signals';

export type RequestStatus = 'idle' | 'pending' | 'fulfilled' | { error: unknown };

/**
 * Reusable feature for tracking request status in signal stores.
 * Based on NgRx Signals manual section 7 - Custom Store Features.
 * 
 * Provides:
 * - State: status tracking (idle/pending/fulfilled/error)
 * - Computed: isLoading, hasError, error signals
 * - Methods: setPending, setFulfilled, setError
 * 
 * @example
 * export const MyStore = signalStore(
 *   withState({ data: [] }),
 *   withRequestStatus(),
 *   withMethods((store) => ({
 *     loadData: rxMethod<void>((trigger$) =>
 *       trigger$.pipe(
 *         tap(store.setPending),
 *         switchMap(() => api.getData()),
 *         tapResponse({
 *           next: (data) => { 
 *             patchState(store, { data }); 
 *             store.setFulfilled(); 
 *           },
 *           error: (err) => store.setError(err)
 *         })
 *       )
 *     )
 *   }))
 * );
 */
export function withRequestStatus() {
  return signalStoreFeature(
    withState<{ status: RequestStatus }>({ 
      status: 'idle' 
    }),
    
    withComputed(({ status }) => ({
      isLoading: () => status() === 'pending',
      
      hasError: () => {
        const s = status();
        return typeof s === 'object' && 'error' in s;
      },
      
      error: () => {
        const s = status();
        return typeof s === 'object' && 'error' in s ? s.error : null;
      },
      
      isIdle: () => status() === 'idle',
      
      isFulfilled: () => status() === 'fulfilled'
    })),
    
    withMethods((store) => ({
      setPending: () => patchState(store, { status: 'pending' as const }),
      
      setFulfilled: () => patchState(store, { status: 'fulfilled' as const }),
      
      setError: (error: unknown) => patchState(store, { status: { error } }),
      
      resetStatus: () => patchState(store, { status: 'idle' as const })
    }))
  );
}

/**
 * Extended version with retry tracking
 */
export function withRetryableRequestStatus() {
  return signalStoreFeature(
    withState<{ 
      status: RequestStatus;
      retryCount: number;
      maxRetries: number;
    }>({ 
      status: 'idle',
      retryCount: 0,
      maxRetries: 3
    }),
    
    withComputed(({ status, retryCount, maxRetries }) => ({
      isLoading: () => status() === 'pending',
      
      hasError: () => {
        const s = status();
        return typeof s === 'object' && 'error' in s;
      },
      
      error: () => {
        const s = status();
        return typeof s === 'object' && 'error' in s ? s.error : null;
      },
      
      canRetry: () => retryCount() < maxRetries(),
      
      retriesRemaining: () => maxRetries() - retryCount()
    })),
    
    withMethods((store) => ({
      setPending: () => patchState(store, { status: 'pending' as const }),
      
      setFulfilled: () => patchState(store, { 
        status: 'fulfilled' as const,
        retryCount: 0 
      }),
      
      setError: (error: unknown) => patchState(store, { status: { error } }),
      
      incrementRetry: () => patchState(store, { 
        retryCount: store.retryCount() + 1 
      }),
      
      resetRetries: () => patchState(store, { 
        retryCount: 0,
        status: 'idle' as const 
      })
    }))
  );
}